import React, { Component, ReactNode, ErrorInfo } from 'react';
import { useTranslation } from 'react-i18next';
import SectionHeading from './SectionHeading';
import '../../theme-components/03-section-heading.css';
import '../../theme-components/06-buttons.css';
import { isChunkLoadError } from '../../utils/chunkErrorHandler';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

const DefaultErrorFallback: React.FC<{ error?: Error; resetError: () => void }> = ({
  error,
  resetError
}) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--color-background-base)' }}>
      <div className="max-w-[560px] w-full text-center">
        {/* Error Card */}
        <div className="themed-card mb-9 relative">
          <div className="card-content-pad">
            {/* Alert Icon */}
            <div className="flex justify-center mb-6">
              <svg
                viewBox="0 0 24 24"
                className="w-14 h-14"
                style={{
                  stroke: 'var(--color-error)',
                  fill: 'none',
                  strokeWidth: '1.5',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round'
                }}
                aria-hidden="true"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>

            {/* Section Heading */}
            <SectionHeading as="h2" className="mb-5">
              System Notice
            </SectionHeading>

            {/* Error Title */}
            <h1
              className="text-2xl font-semibold mb-4 theme-text-primary"
              style={{
                fontFamily: 'var(--font-display)',
                lineHeight: '1.35'
              }}
              role="alert"
              aria-live="assertive"
            >
              {t('errors.somethingWentWrong', 'Something Went Wrong')}
            </h1>

            {/* Error Message */}
            <p
              className="text-base leading-relaxed theme-text-primary mb-6"
              style={{
                fontFamily: 'var(--font-body)',
                lineHeight: '1.6'
              }}
            >
              We encountered an unexpected error. Please try refreshing the page or signing in again.
            </p>

            {/* Technical Details (collapsible) */}
            {error && (
              <details className="mt-6 text-left" open>
                <summary className="cursor-pointer text-sm theme-text-secondary mb-2" style={{fontFamily: 'var(--font-body)'}}>
                  Technical Details
                </summary>
                <div className="p-4 text-xs font-mono overflow-auto max-h-48" style={{
                  background: 'var(--color-surface-subtle)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}>
                  <div className="mb-2">
                    <strong>{t('errors.label', 'Error:')}</strong> {error.message}
                  </div>
                  {error.stack && (
                    <div>
                      <strong>Stack Trace:</strong>
                      <pre className="whitespace-pre-wrap mt-1">{error.stack}</pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-4 w-full">
          {/* Refresh Button (Primary) */}
          <button
            onClick={() => window.location.reload()}
            className="btn btn--primary w-full"
            aria-label="Refresh page"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-[18px] h-[18px] mr-2"
              style={{
                stroke: 'currentColor',
                fill: 'none',
                strokeWidth: '2',
                strokeLinecap: 'round',
                strokeLinejoin: 'round'
              }}
              aria-hidden="true"
            >
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            Refresh Page
          </button>

          {/* Try Again Button (Secondary) */}
          <button
            onClick={resetError}
            className="btn btn--secondary w-full"
            aria-label="Try again"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-[18px] h-[18px] mr-2"
              style={{
                stroke: 'currentColor',
                fill: 'none',
                strokeWidth: '2',
                strokeLinecap: 'round',
                strokeLinejoin: 'round'
              }}
              aria-hidden="true"
            >
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            {t('buttons.tryAgain', 'Try Again')}
          </button>
        </div>
      </div>
    </div>
  );
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false,
      errorId: Date.now().toString(36)
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Chunk-load errors are handled by chunkErrorHandler (auto-reload + toast).
    // Don't surface a visible error UI for them.
    if (isChunkLoadError(error)) {
      return {};
    }
    return {
      hasError: true,
      error,
      errorId: Date.now().toString(36)
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Suppress chunk-load errors entirely — chunkErrorHandler owns recovery.
    if (isChunkLoadError(error)) {
      return;
    }

    console.error('ErrorBoundary caught an error:', {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      errorInfo: {
        componentStack: errorInfo.componentStack
      },
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    this.setState({
      errorInfo
    });

    // Call the onError prop if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: Date.now().toString(36)
    });
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      
      return (
        <FallbackComponent 
          error={this.state.error} 
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export const useErrorHandler = () => {
  const handleError = (error: Error, errorInfo?: any) => {
    console.error('Manual error report:', {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      errorInfo,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  };

  return { handleError };
};

export default ErrorBoundary;